/**
 * PremiumArea — "Who paid the insurer, and did anything come back?"
 *
 * TWO LINKS AND NO FIGURES. `insurance_premiums` and `insurance_premium_discounts` each carry a
 * `transaction_id` and nothing else, so what is recorded here is WHICH payment — the amount and the
 * day are the bank's, read off the row the picker resolved. The side sheet this replaces asked for
 * the premium on a step of its own and the discount on the next one; they are one block because
 * they are one event, and they commit together.
 *
 * NEITHER TABLE PUBLISHES A MERGE KEY, WHICH IS WHY NO INSTRUMENT ROW POINTS HERE.
 * `agreement_merge_data` resolves `insurance.premium_amount` and the two paid keys from the LOAN and
 * its deposits (20260902010000:1108-1113), so the figures the documents print are typed on the
 * Advance canvas. What this canvas is for is the money that left Primelane's account, and the one
 * consequence of recording it is the Unassigned queue.
 *
 * THE QUEUE IS THE WHOLE POINT AND IT IS SAID OUT LOUD. An unlinked transaction sits in Unassigned
 * for ever and nobody goes back for it; recording the premium is what takes it out. Activation does
 * not stop an admin who skips it, which is exactly why the sentence is on the canvas.
 *
 * A PREMIUM ON FILE IS CORRECTED, NOT CLEARED, and the picker says so by offering no clear door: the
 * refusal for that (`validateDraft`) exists because un-recording a premium is what puts a live
 * transaction back in the queue. The discount is genuinely optional, so it keeps its clear — and
 * clearing it is what plans the `delete-discount` statement.
 */

import type { ReactNode } from 'react';

import { Block, Blocks, Hint, Rows } from '../../areas/blocks';
import TransactionPicker from '../../inline/TransactionPicker';
import type { InsuranceFormValues } from '../insuranceForm';

interface Props {
    mode: 'create' | 'edit';
    form: InsuranceFormValues;
    onChange: (updates: Partial<InsuranceFormValues>) => void;
    /** The write's own refusal for one of this area's fields, by FORM FIELD NAME. */
    errors?: Record<string, string>;
    /** The edit commit bar for this area, drawn by the page while the block is dirty. */
    commit?: ReactNode;
}

export default function PremiumArea({ mode, form, onChange, errors, commit }: Props) {
    return (
        <Blocks>
            <Block id="b-premium" title="Premium payment" aside="required">
                <p className="sd-lede sd-dim">
                    The debit that paid the insurer for this year, and the credit the insurer
                    returned if anything came back. Both are links to payments the bank already
                    recorded — nothing here types an amount.
                </p>

                <Rows>
                    <TransactionPicker
                        id="f-premium"
                        label="Payment to the insurer"
                        type="DEBIT"
                        value={form.premiumTransactionId}
                        onChange={(value) => onChange({ premiumTransactionId: value })}
                        error={errors?.premiumTransactionId}
                        emptyLabel="Not recorded"
                        hint="the debit that paid the policy"
                    />
                    {/* THE ONE CLEAR DOOR ON THIS CANVAS. A discount that was linked and is now not
                        is a link to REMOVE — the save deletes the row rather than leaving it behind
                        pointing at a transaction this year no longer claims. */}
                    <TransactionPicker
                        id="f-discount"
                        label="Discount"
                        type="CREDIT"
                        value={form.discountTransactionId}
                        onChange={(value) => onChange({ discountTransactionId: value })}
                        onClear={() => onChange({ discountTransactionId: '' })}
                        error={errors?.discountTransactionId}
                        emptyLabel="None recorded"
                        hint="the credit the insurer returned"
                    />
                </Rows>

                <Hint className="mt-3">
                    Both links commit together. Recording the premium is what takes its bank
                    transaction out of the Unassigned queue — until it is linked the transaction
                    sits there for ever, nobody goes back for it, and nothing else on this page
                    stops you.
                </Hint>

                {/* `UNIQUE (transaction_id)` ON BOTH TABLES, STATED BEFORE IT IS HIT. The server's
                    refusal names the collision after the fact; this says why one exists. */}
                <Hint className="mt-2">
                    One debit records one premium. A payment already recorded against another policy
                    is refused here — the same money cannot have bought two years of cover, and a
                    discount belongs to the premium it came back from.
                </Hint>

                {mode === 'create' && (
                    <Hint className="mt-2">
                        Nothing is written until the policy is created, and that happens on the
                        Advance area — where the premium the year is written over is stated.
                    </Hint>
                )}
            </Block>

            {/* THE BAR IS THE CANVAS'S LAST CHILD AND NOT THE BLOCK'S, as on every other dossier
                area. `.sd-commit` is sticky to the bottom of its containing block, so a bar nested
                inside `b-premium` scrolls away with that block instead of standing over the canvas. */}
            {commit}
        </Blocks>
    );
}
